import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Upload, Dumbbell, Trophy, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminLayout from '../../components/AdminLayout';
import api from '../../utils/api';
import { toast } from 'sonner';

const FLOW_TEMPLATES = [
  { value: 'AMATEUR_SPORTS_V1', label: 'Amateur Sports Application', icon: Trophy, description: 'Youth leagues, clubs, tournaments, camps' },
  { value: 'BOUTIQUE_FITNESS_V1', label: 'Boutique Fitness Club Application', icon: Dumbbell, description: 'Yoga, pilates, HIIT, spin studios' }
];

// Helper function to get the correct logo URL
const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  
  // If it's already a full external URL (not from our uploads), return as-is
  if (logoUrl.startsWith('http') && !logoUrl.includes('/api/uploads/')) {
    return logoUrl;
  }
  
  // Extract file_id from various URL formats
  const uploadMatch = logoUrl.match(/\/api\/uploads\/([a-f0-9]+)/);
  if (uploadMatch) {
    const fileId = uploadMatch[1];
    return `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${fileId}`;
  }
  
  // If it's just a file_id
  if (/^[a-f0-9]{24}$/.test(logoUrl)) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${logoUrl}`;
  }
  
  return logoUrl;
};

const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    logo_url: '',
    contact_email: '',
    contact_name: '',
    active: true,
    flow_template: 'AMATEUR_SPORTS_V1',
    theme: 'light',
    payout_per_quote: '',
    portal_password: ''
  });

  useEffect(() => {
    loadAffiliates();
  }, []);

  const loadAffiliates = async () => {
    try {
      const response = await api.get('/affiliates');
      setAffiliates(response.data);
    } catch (error) {
      toast.error('Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await api.post('/uploads', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Create a temporary URL for the uploaded file
      const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${response.data.file_id}`;
      setUploadedLogoUrl(logoUrl);
      setFormData({ ...formData, logo_url: logoUrl });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      payout_per_quote:
        formData.payout_per_quote === '' || formData.payout_per_quote == null
          ? null
          : parseFloat(formData.payout_per_quote),
    };
    try {
      if (editingAffiliate) {
        await api.put(`/affiliates/${editingAffiliate.id}`, payload);
        toast.success('Affiliate updated successfully');
      } else {
        await api.post('/affiliates', payload);
        toast.success('Affiliate created successfully');
      }
      setDialogOpen(false);
      setEditingAffiliate(null);
      resetForm();
      loadAffiliates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save affiliate');
    }
  };

  const handleEdit = (affiliate) => {
    setEditingAffiliate(affiliate);
    setFormData({
      name: affiliate.name,
      slug: affiliate.slug,
      logo_url: affiliate.logo_url || '',
      contact_email: affiliate.contact_email,
      contact_name: affiliate.contact_name,
      active: affiliate.active,
      flow_template: affiliate.flow_template || 'AMATEUR_SPORTS_V1',
      theme: affiliate.theme || 'light',
      payout_per_quote: affiliate.payout_per_quote != null ? String(affiliate.payout_per_quote) : '',
      portal_password: ''
    });
    setUploadedLogoUrl(affiliate.logo_url || '');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/affiliates/${id}`);
      toast.success('Affiliate deleted successfully');
      loadAffiliates();
    } catch (error) {
      toast.error('Failed to delete affiliate');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      logo_url: '',
      contact_email: '',
      contact_name: '',
      active: true,
      flow_template: 'AMATEUR_SPORTS_V1',
      theme: 'light',
      payout_per_quote: '',
      portal_password: ''
    });
    setUploadedLogoUrl('');
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingAffiliate(null);
      resetForm();
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B6FE8]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-[#1A1A1A] uppercase tracking-wide" style={{fontFamily: "Barlow Condensed, sans-serif"}}>Affiliates</h1>
            <p className="text-[#6B7280] mt-1">Manage your partner organizations</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-affiliate-btn"
                className="bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] font-medium"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Affiliate
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="text-lg font-semibold text-[#1A1A1A]">{editingAffiliate ? 'Edit Affiliate' : 'Create Affiliate'}</DialogTitle>
                {editingAffiliate && (
                  <div className="mt-2 p-3 bg-[#EEF0FF] rounded-lg border border-[#C4BFFF]">
                    <p className="text-sm text-[#6B7280] mb-1">Landing Page URL:</p>
                    <a 
                      href={`/${formData.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#7B6FE8] hover:text-[#6B5CE7] hover:underline font-medium flex items-center gap-1"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {window.location.origin}/{formData.slug}
                    </a>
                  </div>
                )}
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto space-y-4 mt-4 pr-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-[#1A1A1A] font-medium">Name *</Label>
                    <Input
                      id="name"
                      data-testid="affiliate-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="slug" className="text-[#1A1A1A] font-medium">Slug *</Label>
                    <Input
                      id="slug"
                      data-testid="affiliate-slug-input"
                      value={formData.slug}
                      onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                      required
                      className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                      placeholder="partner-name"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#1A1A1A] font-medium">Partner Logo</Label>
                  <p className="text-sm text-[#6B7280] mt-1 mb-3">
                    Upload a logo image (PNG, JPG, SVG - max 5MB)
                  </p>
                  
                  {uploadedLogoUrl || formData.logo_url ? (
                    <div className="border border-[#E5E7EB] rounded-lg p-4 mb-3">
                      <div className="flex items-center gap-4">
                        <img 
                          src={getLogoUrl(uploadedLogoUrl || formData.logo_url)} 
                          alt="Partner logo preview" 
                          className="h-16 w-auto object-contain bg-[#F7F8FA] rounded p-2"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#1A1A1A]">Logo uploaded</p>
                          <p className="text-xs text-[#6B7280] mt-1 break-all">{uploadedLogoUrl || formData.logo_url}</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-[#E5E7EB] text-[#1A1A1A] hover:bg-[#F3F4F6]"
                          onClick={() => {
                            setUploadedLogoUrl('');
                            setFormData({...formData, logo_url: ''});
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  
                  <div className="border-2 border-dashed border-[#E5E7EB] rounded-lg p-6 text-center hover:border-[#7B6FE8] transition-colors">
                    <input
                      type="file"
                      id="logo_upload"
                      data-testid="affiliate-logo-upload"
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                    />
                    <label htmlFor="logo_upload">
                      <div className="cursor-pointer">
                        <Upload className="h-10 w-10 text-[#9CA3AF] mx-auto mb-3" />
                        <Button
                          type="button"
                          variant="outline"
                          disabled={uploading}
                          className="border-[#E5E7EB] text-[#1A1A1A] hover:bg-[#F3F4F6]"
                          asChild
                        >
                          <span>
                            {uploading ? 'Uploading...' : 'Choose Logo File'}
                          </span>
                        </Button>
                        <p className="text-xs text-[#9CA3AF] mt-2">or enter URL manually below</p>
                      </div>
                    </label>
                  </div>
                  
                  <div className="mt-3">
                    <Label htmlFor="logo_url" className="text-[#1A1A1A] font-medium">Or Enter Logo URL</Label>
                    <Input
                      id="logo_url"
                      data-testid="affiliate-logo-input"
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                      className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_name" className="text-[#1A1A1A] font-medium">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      data-testid="affiliate-contact-name-input"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      required
                      className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email" className="text-[#1A1A1A] font-medium">Contact Email *</Label>
                    <Input
                      id="contact_email"
                      data-testid="affiliate-contact-email-input"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                      required
                      className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                    />
                  </div>
                </div>

                {/* Flow Template Selection */}
                <div className="space-y-3">
                  <Label className="text-[#1A1A1A] font-medium">Application Flow Template *</Label>
                  <p className="text-sm text-[#6B7280]">
                    Select which application wizard users will see on this landing page
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    {FLOW_TEMPLATES.map((template) => {
                      const Icon = template.icon;
                      const isSelected = formData.flow_template === template.value;
                      return (
                        <div
                          key={template.value}
                          data-testid={`flow-template-${template.value}`}
                          onClick={() => setFormData({...formData, flow_template: template.value})}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-[#7B6FE8] bg-[#EEF0FF]' 
                              : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${
                              isSelected ? 'bg-[#DBEAFE]' : 'bg-[#F3F4F6]'
                            }`}>
                              <Icon className={`h-5 w-5 ${
                                isSelected ? 'text-[#7B6FE8]' : 'text-[#6B7280]'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className={`font-medium ${
                                  isSelected ? 'text-[#6B5CE7]' : 'text-[#1A1A1A]'
                                }`}>
                                  {template.label}
                                </p>
                                {isSelected && (
                                  <span className="text-xs bg-[#7B6FE8] text-white px-2 py-0.5 rounded-full">
                                    Selected
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-[#6B7280] mt-1">
                                {template.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Theme Selection */}
                <div className="space-y-2">
                  <Label className="text-[#1A1A1A] font-medium">Landing Page Theme</Label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, theme: 'light'})}
                      data-testid="theme-light-btn"
                      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all ${
                        formData.theme === 'light'
                          ? 'border-[#7B6FE8] bg-[#EEF0FF] text-[#6B5CE7]'
                          : 'border-[#E5E7EB] hover:border-[#D1D5DB] text-[#6B7280]'
                      }`}
                    >
                      <Sun className="h-4 w-4" />
                      <span className="font-medium text-sm">Light</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, theme: 'dark'})}
                      data-testid="theme-dark-btn"
                      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all ${
                        formData.theme === 'dark'
                          ? 'border-[#7B6FE8] bg-[#232323] text-white'
                          : 'border-[#E5E7EB] hover:border-[#D1D5DB] text-[#6B7280]'
                      }`}
                    >
                      <Moon className="h-4 w-4" />
                      <span className="font-medium text-sm">Dark</span>
                    </button>
                  </div>
                </div>

                {/* Partner Portal */}
                <div className="space-y-3 border-t border-[#E5E7EB] pt-4">
                  <div>
                    <Label className="text-[#1A1A1A] font-medium">Partner Portal</Label>
                    <p className="text-sm text-[#6B7280] mt-1">
                      Give this partner a password-protected link to track every org that applied through their page and what stage they reached.
                    </p>
                  </div>
                  {editingAffiliate && formData.slug && (
                    <div className="p-3 bg-[#F0FDF4] rounded-lg border border-[#A7F3D0]">
                      <p className="text-sm text-[#065F46] mb-1">Partner Portal URL:</p>
                      <a
                        href={`/partner/${formData.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="partner-portal-link"
                        className="text-sm text-[#047857] hover:underline font-medium break-all"
                      >
                        {window.location.origin}/partner/{formData.slug}
                      </a>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="payout_per_quote" className="text-[#1A1A1A] font-medium">Payout per Quote ($)</Label>
                      <Input
                        id="payout_per_quote"
                        data-testid="affiliate-payout-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.payout_per_quote}
                        onChange={(e) => setFormData({ ...formData, payout_per_quote: e.target.value })}
                        className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                        placeholder="e.g. 50"
                      />
                      <p className="text-xs text-[#9CA3AF] mt-1">Used to estimate partner earnings in their portal.</p>
                    </div>
                    <div>
                      <Label htmlFor="portal_password" className="text-[#1A1A1A] font-medium">Portal Password</Label>
                      <Input
                        id="portal_password"
                        data-testid="affiliate-portal-password-input"
                        type="text"
                        value={formData.portal_password}
                        onChange={(e) => setFormData({ ...formData, portal_password: e.target.value })}
                        className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                        placeholder={editingAffiliate ? 'Leave blank to keep current' : 'Set a password'}
                      />
                      <p className="text-xs text-[#9CA3AF] mt-1">Share this with the partner to access their portal.</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    data-testid="affiliate-active-switch"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({...formData, active: checked})}
                  />
                  <Label htmlFor="active" className="cursor-pointer text-[#1A1A1A]">Active</Label>
                </div>
                </div>

                {/* Fixed Footer with Submit Button */}
                <div className="flex-shrink-0 pt-4 mt-4 border-t border-[#E5E7EB]">
                  <Button 
                    type="submit"
                    data-testid="save-affiliate-btn"
                    className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] font-medium"
                  >
                    {editingAffiliate ? 'Update Affiliate' : 'Create Affiliate'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="bg-white border border-[#E5E7EB] shadow-none">
          <CardHeader className="border-b border-[#E5E7EB] bg-[#F7F8FA]">
            <CardTitle className="text-base font-semibold text-[#1A1A1A]">{affiliates.length} Affiliates</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {affiliates.length === 0 ? (
              <p className="text-[#6B7280] text-center py-8">No affiliates yet</p>
            ) : (
              <div className="divide-y divide-[#E5E7EB]">
                {affiliates.map((affiliate) => {
                  const flowTemplate = FLOW_TEMPLATES.find(t => t.value === affiliate.flow_template) || FLOW_TEMPLATES[0];
                  const FlowIcon = flowTemplate.icon;
                  return (
                  <div
                    key={affiliate.id}
                    data-testid={`affiliate-${affiliate.id}`}
                    className="p-4 hover:bg-[#F3F4F6] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        {affiliate.logo_url && (
                          <img 
                            src={getLogoUrl(affiliate.logo_url)} 
                            alt={affiliate.name} 
                            className="h-10 w-10 object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold text-[#1A1A1A]">{affiliate.name}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full border ${
                              affiliate.active 
                                ? 'bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]' 
                                : 'bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]'
                            }`}>
                              {affiliate.active ? 'Active' : 'Inactive'}
                            </span>
                            {/* Flow Template Badge */}
                            <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 border ${
                              affiliate.flow_template === 'BOUTIQUE_FITNESS_V1'
                                ? 'bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]'
                                : 'bg-[#EEF0FF] text-[#6B5CE7] border-[#C4BFFF]'
                            }`}>
                              <FlowIcon className="h-3 w-3" />
                              {affiliate.flow_template === 'BOUTIQUE_FITNESS_V1' ? 'Fitness' : 'Sports'}
                            </span>
                          </div>
                          
                          {/* Landing Page URL */}
                          <div className="mb-2 flex items-center gap-2">
                            <a 
                              href={`/${affiliate.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-[#7B6FE8] hover:text-[#6B5CE7] hover:underline font-medium flex items-center gap-1"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              /{affiliate.slug}
                            </a>
                          </div>
                          
                          <p className="text-sm text-[#6B7280]">
                            {affiliate.contact_name} • {affiliate.contact_email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`edit-affiliate-${affiliate.id}-btn`}
                          variant="ghost"
                          size="sm"
                          className="text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6]"
                          onClick={() => handleEdit(affiliate)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              data-testid={`delete-affiliate-${affiliate.id}-btn`}
                              variant="ghost"
                              size="sm"
                              className="text-[#991B1B] hover:text-[#991B1B] hover:bg-[#FEF2F2]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-[#1A1A1A]">Delete Affiliate</AlertDialogTitle>
                              <AlertDialogDescription className="text-[#6B7280]">
                                Are you sure you want to delete {affiliate.name}? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-[#E5E7EB] text-[#1A1A1A] hover:bg-[#F3F4F6]">Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(affiliate.id)}
                                className="bg-[#991B1B] text-white hover:bg-[#7F1D1D]"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminAffiliates;
